import {
  CalendarOutlined,
  CheckCircleOutlined,
  EllipsisOutlined,
  FilterOutlined,
  HolderOutlined,
  PlusOutlined,
  ReloadOutlined,
  SearchOutlined,
  StopOutlined,
} from '@ant-design/icons';
import type { MenuProps, TableProps } from 'antd';
import { Button, DatePicker, Dropdown, Input, Radio, Select, Switch, Table } from 'antd';
import type { ColumnsType, TableRowSelection } from 'antd/es/table/interface';
import dayjs, { type Dayjs } from 'dayjs';
import { useCallback, useMemo, useState } from 'react';
import { PreviewImage } from '@/components/PreviewImage/PreviewImage';
import { PageHeader } from '@/components/PageHeader/PageHeader';
import { useGlobalLoader } from '@/hooks/useGlobalLoader';
import { useNotify } from '@/hooks/useNotify';
import { useLazyGetCategoriesQuery } from '@/modules/categories/categoriesApi';
import {
  useBulkDeleteSubcategoriesMutation,
  useBulkUpdateSubcategoryStatusMutation,
  useDeleteSubcategoryMutation,
  useGetSubcategoriesQuery,
  useReorderSubcategoriesMutation,
  useUpdateSubcategoryMutation,
} from '@/modules/subcategories/subcategoriesApi';
import type { Subcategory, SubcategoryStatusFilter } from '@/types/subcategory';
import { resolveMutationResult } from '@/utils/apiResponse';
import { confirmBulkDelete, confirmDelete } from '@/utils/confirmDialog';
import { formatDateTime } from '@/utils/date';
import { getSubcategoryImageUrl } from '@/utils/media';
import { reorderById } from '@/utils/reorder';
import { ucfirst } from '@/utils/string';
import { ColumnFilterPanel } from '../categories/components/ColumnFilterPanel';
import { SortableColumnTitle } from '../categories/components/SortableColumnTitle';
import { SubcategoriesTableSkeleton } from './components/SubcategoriesTableSkeleton';
import { SubcategoryFormModal } from './components/SubcategoryFormModal';
import { useSubcategoryQueryParams } from './hooks/useSubcategoryQueryParams';
import styles from './Subcategories.module.scss';

export default function Subcategories() {
  const notify = useNotify();
  const { state, apiParams, patchParams, resetAll, hasActiveFiltersOrSort } =
    useSubcategoryQueryParams();
  const { data: listResult, isLoading, isFetching } = useGetSubcategoriesQuery(apiParams);
  useGlobalLoader(isFetching && !!listResult);

  const [fetchCategories, { data: categoriesResult, isFetching: categoriesLoading }] =
    useLazyGetCategoriesQuery();

  const subcategories = useMemo(() => listResult?.results ?? [], [listResult?.results]);
  const activeSortBy = listResult?.sortBy ?? state.sortBy;
  const activeSortOrder = listResult?.sortOrder ?? state.sortOrder;
  const total = listResult?.total ?? 0;
  const currentPage = Number(listResult?.page ?? state.page);
  const pageSize = Number(listResult?.limit ?? state.limit);

  const singleCategoryId = state.categoryIds.length === 1 ? state.categoryIds[0] : undefined;

  const canDrag =
    state.sortBy === 'sortOrder' &&
    !state.search &&
    state.status === 'all' &&
    !state.dateFrom &&
    !state.dateTo &&
    state.categoryIds.length <= 1;

  const { data: categorySortListResult } = useGetSubcategoriesQuery(
    {
      categoryIds: singleCategoryId ? [singleCategoryId] : undefined,
      page: 1,
      limit: 1000,
      sortBy: 'sortOrder',
      sortOrder: 'asc',
    },
    { skip: !canDrag || !singleCategoryId },
  );

  const [searchDraft, setSearchDraft] = useState(state.search);
  const [statusDraft, setStatusDraft] = useState<SubcategoryStatusFilter>(state.status);
  const [categoryDraft, setCategoryDraft] = useState<string[]>(state.categoryIds);
  const [dateDraft, setDateDraft] = useState<[Dayjs | null, Dayjs | null] | null>(() =>
    state.dateFrom || state.dateTo
      ? [state.dateFrom ? dayjs(state.dateFrom) : null, state.dateTo ? dayjs(state.dateTo) : null]
      : null,
  );

  const [updateSubcategory] = useUpdateSubcategoryMutation();
  const [deleteSubcategory] = useDeleteSubcategoryMutation();
  const [bulkUpdateStatus, { isLoading: isBulkUpdating }] =
    useBulkUpdateSubcategoryStatusMutation();
  const [bulkDelete, { isLoading: isBulkDeleting }] = useBulkDeleteSubcategoriesMutation();
  const [reorderSubcategories, { isLoading: isReordering }] = useReorderSubcategoriesMutation();
  const [modalOpen, setModalOpen] = useState(false);
  const [editingSubcategory, setEditingSubcategory] = useState<Subcategory | null>(null);
  const [dragId, setDragId] = useState<string | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([]);

  const categoryFilterOptions = useMemo(() => {
    const categories = categoriesResult?.results ?? [];
    const options = categories.map((category) => ({
      label: ucfirst(category.name),
      value: category.id,
    }));

    state.categoryIds.forEach((categoryId) => {
      if (!options.some((option) => option.value === categoryId)) {
        const name =
          subcategories.find((item) => item.categoryId === categoryId)?.categoryName ?? categoryId;
        options.unshift({ label: ucfirst(name), value: categoryId });
      }
    });

    return options;
  }, [categoriesResult?.results, state.categoryIds, subcategories]);

  const handleSort = useCallback(
    (field: string) => {
      const nextOrder: 'asc' | 'desc' =
        state.sortBy === field ? (state.sortOrder === 'asc' ? 'desc' : 'asc') : 'asc';
      patchParams({ sortBy: field, sortOrder: nextOrder, page: 1 });
    },
    [patchParams, state.sortBy, state.sortOrder],
  );

  const openCreate = () => {
    setEditingSubcategory(null);
    setModalOpen(true);
  };

  const openEdit = useCallback((subcategory: Subcategory) => {
    setEditingSubcategory(subcategory);
    setModalOpen(true);
  }, []);

  const closeModal = () => {
    setModalOpen(false);
    setEditingSubcategory(null);
  };

  const clearSelection = () => setSelectedRowKeys([]);

  const handleResetFiltersAndSort = () => {
    setSearchDraft('');
    setStatusDraft('all');
    setCategoryDraft([]);
    setDateDraft(null);
    resetAll();
  };

  const handleStatusChange = useCallback(
    async (subcategory: Subcategory, isActive: boolean) => {
      const result = await resolveMutationResult(
        updateSubcategory({ id: subcategory.id, body: { isActive } }),
      );

      if (result.ok) {
        notify.success(result.message ?? 'Status updated.');
        return;
      }

      notify.error(result.message);
    },
    [notify, updateSubcategory],
  );

  const handleDelete = useCallback(
    async (subcategory: Subcategory) => {
      const confirmed = await confirmDelete(subcategory.name);
      if (!confirmed) return;

      const result = await resolveMutationResult(deleteSubcategory(subcategory.id));
      if (result.ok) {
        notify.success(result.message ?? 'Subcategory deleted.');
        setSelectedRowKeys((keys) => keys.filter((key) => key !== subcategory.id));
        return;
      }

      notify.error(result.message);
    },
    [deleteSubcategory, notify],
  );

  const handleBulkStatus = async (isActive: boolean) => {
    if (selectedRowKeys.length === 0) return;

    const result = await resolveMutationResult(
      bulkUpdateStatus({ ids: selectedRowKeys, isActive }),
    );

    if (result.ok) {
      notify.success(result.message ?? 'Subcategories updated.');
      clearSelection();
      return;
    }

    notify.error(result.message);
  };

  const handleBulkDelete = async () => {
    if (selectedRowKeys.length === 0) return;

    const confirmed = await confirmBulkDelete(selectedRowKeys.length);
    if (!confirmed) return;

    const result = await resolveMutationResult(bulkDelete({ ids: selectedRowKeys }));
    if (result.ok) {
      notify.success(result.message ?? 'Subcategories deleted.');
      clearSelection();
      return;
    }

    notify.error(result.message);
  };

  const handleDrop = async (dropId: string) => {
    if (!canDrag || !dragId || dragId === dropId) {
      setDragId(null);
      return;
    }

    const dragged = subcategories.find((item) => item.id === dragId);
    const dropped = subcategories.find((item) => item.id === dropId);

    if (!dragged || !dropped || dragged.categoryId !== dropped.categoryId) {
      notify.error('You can only reorder subcategories within the same category.');
      setDragId(null);
      return;
    }

    const scope = singleCategoryId
      ? (categorySortListResult?.results ?? subcategories)
      : subcategories.filter((item) => item.categoryId === dragged.categoryId);
    const reordered = reorderById(scope, dragId, dropId);
    setDragId(null);

    const result = await resolveMutationResult(
      reorderSubcategories({ ids: reordered.map((item) => item.id) }),
    );

    if (result.ok) {
      notify.success(result.message ?? 'Order updated.');
      return;
    }

    notify.error(result.message);
  };

  const handleTableChange: TableProps<Subcategory>['onChange'] = (pagination) => {
    const pageChanged = pagination.current !== undefined && pagination.current !== state.page;
    const limitChanged = pagination.pageSize !== undefined && pagination.pageSize !== state.limit;

    if (!pageChanged && !limitChanged) return;

    patchParams({
      ...(pageChanged && pagination.current !== undefined ? { page: pagination.current } : {}),
      ...(limitChanged && pagination.pageSize !== undefined ? { limit: pagination.pageSize } : {}),
    });
  };

  const getRowActionItems = useCallback(
    (subcategory: Subcategory): MenuProps['items'] => [
      { key: 'edit', label: 'Edit', onClick: () => openEdit(subcategory) },
      { type: 'divider' },
      { key: 'delete', label: 'Delete', danger: true, onClick: () => handleDelete(subcategory) },
    ],
    [handleDelete, openEdit],
  );

  const columns: ColumnsType<Subcategory> = useMemo(
    () => [
      {
        title: '',
        key: 'drag',
        width: 40,
        className: styles.dragCol,
        render: () => (canDrag ? <HolderOutlined className={styles.dragHandle} /> : null),
      },
      {
        title: (
          <SortableColumnTitle
            label="NAME"
            field="name"
            activeField={activeSortBy}
            sortOrder={activeSortOrder}
            onSort={handleSort}
          />
        ),
        dataIndex: 'name',
        key: 'name',
        className: styles.nameCol,
        filterIcon: (filtered) => (
          <SearchOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filteredValue: state.search ? [state.search] : null,
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel
            onApply={() => {
              patchParams({ search: searchDraft.trim() || null }, { resetPage: true });
              close();
            }}
            onReset={() => {
              setSearchDraft('');
              patchParams({ search: null }, { resetPage: true });
              close();
            }}
          >
            <Input
              allowClear
              size="small"
              prefix={<SearchOutlined />}
              placeholder="Search name or slug"
              value={searchDraft}
              onChange={(event) => setSearchDraft(event.target.value)}
              onPressEnter={() => {
                patchParams({ search: searchDraft.trim() || null }, { resetPage: true });
                close();
              }}
            />
          </ColumnFilterPanel>
        ),
        filterDropdownProps: {
          onOpenChange: (open) => {
            if (open) setSearchDraft(state.search);
          },
        },
        render: (name: string, record) => (
          <div className={styles.nameRow}>
            <PreviewImage
              src={getSubcategoryImageUrl(record.image)}
              size={32}
              className={styles.thumb}
              fallback={ucfirst(name).charAt(0)}
            />
            <div className={styles.nameBlock}>
              <span className={styles.nameCell}>{ucfirst(name)}</span>
              <span className={styles.slug}>{record.slug}</span>
            </div>
          </div>
        ),
      },
      {
        title: (
          <SortableColumnTitle
            label="CATEGORY"
            field="categoryId"
            activeField={activeSortBy}
            sortOrder={activeSortOrder}
            onSort={handleSort}
          />
        ),
        dataIndex: 'categoryId',
        key: 'categoryId',
        className: styles.categoryCol,
        filterIcon: (filtered) => (
          <FilterOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filteredValue: state.categoryIds.length ? state.categoryIds : null,
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel
            onApply={() => {
              patchParams(
                { categoryIds: categoryDraft.length ? categoryDraft : null },
                { resetPage: true },
              );
              close();
            }}
            onReset={() => {
              setCategoryDraft([]);
              patchParams({ categoryIds: null }, { resetPage: true });
              close();
            }}
          >
            <Select
              allowClear
              showSearch
              mode="multiple"
              size="small"
              className={styles.categorySelect}
              placeholder="All categories"
              optionFilterProp="label"
              options={categoryFilterOptions}
              loading={categoriesLoading}
              value={categoryDraft}
              onChange={(value) => setCategoryDraft(value)}
              onOpenChange={(visible) => {
                if (visible) {
                  void fetchCategories({
                    page: 1,
                    limit: 100,
                    sortBy: 'name',
                    sortOrder: 'asc',
                  });
                }
              }}
            />
          </ColumnFilterPanel>
        ),
        filterDropdownProps: {
          onOpenChange: (open) => {
            if (open) {
              setCategoryDraft(state.categoryIds);
              void fetchCategories({
                page: 1,
                limit: 100,
                sortBy: 'name',
                sortOrder: 'asc',
              });
            }
          },
        },
        render: (_categoryId: string, record) => (
          <span className={styles.categoryCell}>{ucfirst(record.categoryName) || '—'}</span>
        ),
      },
      {
        title: (
          <SortableColumnTitle
            label="STATUS"
            field="isActive"
            activeField={activeSortBy}
            sortOrder={activeSortOrder}
            onSort={handleSort}
          />
        ),
        dataIndex: 'isActive',
        key: 'isActive',
        align: 'center',
        filterIcon: (filtered) => (
          <FilterOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filteredValue: state.status !== 'all' ? [state.status] : null,
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel
            onApply={() => {
              patchParams(
                { status: statusDraft === 'all' ? null : statusDraft },
                { resetPage: true },
              );
              close();
            }}
            onReset={() => {
              setStatusDraft('all');
              patchParams({ status: null }, { resetPage: true });
              close();
            }}
          >
            <Radio.Group
              value={statusDraft}
              onChange={(event) => setStatusDraft(event.target.value)}
              className={styles.statusRadio}
            >
              <Radio value="all">All</Radio>
              <Radio value="active">Active</Radio>
              <Radio value="inactive">Inactive</Radio>
            </Radio.Group>
          </ColumnFilterPanel>
        ),
        filterDropdownProps: {
          onOpenChange: (open) => {
            if (open) setStatusDraft(state.status);
          },
        },
        render: (isActive: boolean, record) => (
          <Switch
            checked={isActive}
            size="small"
            onChange={(checked) => handleStatusChange(record, checked)}
          />
        ),
      },
      {
        title: (
          <SortableColumnTitle
            label="SORT"
            field="sortOrder"
            activeField={activeSortBy}
            sortOrder={activeSortOrder}
            onSort={handleSort}
          />
        ),
        dataIndex: 'sortOrder',
        key: 'sortOrder',
        align: 'center',
        render: (value: number) => <span className={styles.sortNum}>{value + 1}</span>,
      },
      {
        title: (
          <SortableColumnTitle
            label="UPDATED"
            field="updatedAt"
            activeField={activeSortBy}
            sortOrder={activeSortOrder}
            onSort={handleSort}
          />
        ),
        dataIndex: 'updatedAt',
        key: 'updatedAt',
        filterIcon: (filtered) => (
          <CalendarOutlined className={filtered ? styles.filterActive : styles.filterIcon} />
        ),
        filteredValue: state.dateFrom || state.dateTo ? ['filtered'] : null,
        filterDropdown: ({ close }) => (
          <ColumnFilterPanel
            onApply={() => {
              patchParams(
                {
                  dateFrom: dateDraft?.[0]?.format('YYYY-MM-DD') || null,
                  dateTo: dateDraft?.[1]?.format('YYYY-MM-DD') || null,
                },
                { resetPage: true },
              );
              close();
            }}
            onReset={() => {
              setDateDraft(null);
              patchParams({ dateFrom: null, dateTo: null }, { resetPage: true });
              close();
            }}
          >
            <DatePicker.RangePicker
              size="small"
              value={dateDraft}
              onChange={(values: [Dayjs | null, Dayjs | null] | null) => setDateDraft(values)}
              style={{ width: '100%' }}
            />
          </ColumnFilterPanel>
        ),
        filterDropdownProps: {
          onOpenChange: (open) => {
            if (open) {
              setDateDraft(
                state.dateFrom || state.dateTo
                  ? [
                      state.dateFrom ? dayjs(state.dateFrom) : null,
                      state.dateTo ? dayjs(state.dateTo) : null,
                    ]
                  : null,
              );
            }
          },
        },
        render: (value: string) => <span className={styles.dateCell}>{formatDateTime(value)}</span>,
      },
      {
        title: 'ACTION',
        key: 'actions',
        align: 'center',
        render: (_value, record) => (
          <Dropdown
            menu={{ items: getRowActionItems(record) }}
            trigger={['click']}
            placement="bottomRight"
          >
            <Button
              type="text"
              size="small"
              className={styles.actionBtn}
              icon={<EllipsisOutlined />}
              aria-label="Open actions"
              onClick={(event) => event.stopPropagation()}
            />
          </Dropdown>
        ),
      },
    ],
    [
      handleSort,
      activeSortBy,
      activeSortOrder,
      state.search,
      state.status,
      state.categoryIds,
      state.dateFrom,
      state.dateTo,
      canDrag,
      searchDraft,
      patchParams,
      statusDraft,
      categoryDraft,
      categoryFilterOptions,
      categoriesLoading,
      fetchCategories,
      handleStatusChange,
      dateDraft,
      getRowActionItems,
    ],
  );

  const rowSelection: TableRowSelection<Subcategory> = {
    columnWidth: 40,
    selectedRowKeys,
    onChange: (keys) => setSelectedRowKeys(keys as string[]),
  };

  const showSkeleton = isLoading && !listResult;
  const mutationLoading = isReordering || isBulkUpdating || isBulkDeleting;
  const hasSelection = selectedRowKeys.length > 0;

  return (
    <div className={styles.page}>
      <PageHeader
        actions={
          <>
            {hasActiveFiltersOrSort ? (
              <Button icon={<ReloadOutlined />} onClick={handleResetFiltersAndSort}>
                Reset
              </Button>
            ) : null}
            <Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>
              Add subcategory
            </Button>
          </>
        }
      />

      {hasSelection ? (
        <div className={styles.bulkBar}>
          <span className={styles.bulkCount}>{selectedRowKeys.length} selected</span>
          <Button
            size="small"
            icon={<CheckCircleOutlined />}
            onClick={() => handleBulkStatus(true)}
            loading={isBulkUpdating}
          >
            Activate
          </Button>
          <Button
            size="small"
            icon={<StopOutlined />}
            onClick={() => handleBulkStatus(false)}
            loading={isBulkUpdating}
          >
            Deactivate
          </Button>
          <Button size="small" danger onClick={handleBulkDelete} loading={isBulkDeleting}>
            Delete
          </Button>
          <Button size="small" type="text" onClick={clearSelection}>
            Clear
          </Button>
        </div>
      ) : null}

      <div className={styles.tableCard}>
        {showSkeleton ? (
          <SubcategoriesTableSkeleton rows={pageSize} />
        ) : (
          <Table<Subcategory>
            className={styles.table}
            rowKey="id"
            size="small"
            loading={mutationLoading}
            columns={columns}
            dataSource={subcategories}
            rowSelection={rowSelection}
            onChange={handleTableChange}
            pagination={{
              current: currentPage,
              pageSize,
              total,
              showSizeChanger: true,
              pageSizeOptions: [10, 20, 50],
              showTotal: (total, [start, end]) => `${start}-${end} of ${total}`,
              size: 'small',
            }}
            onRow={(record) => ({
              draggable: canDrag,
              className: dragId === record.id ? styles.draggingRow : undefined,
              onDragStart: () => setDragId(record.id),
              onDragEnd: () => setDragId(null),
              onDragOver: (event) => event.preventDefault(),
              onDrop: () => handleDrop(record.id),
            })}
          />
        )}
      </div>

      <SubcategoryFormModal
        open={modalOpen}
        subcategory={editingSubcategory}
        defaultCategoryId={singleCategoryId}
        onClose={closeModal}
      />
    </div>
  );
}
